import { ThemeEvents, CartUpdateEvent, CartAddEvent } from '@theme/events';

if(!window.customElements.get('cart-progress')) {
  class CartProgress extends HTMLElement {
    constructor() {
      super();
      this.checkoutButton = document.querySelector('button[name="checkout"]');
      this.thresholdOne = parseFloat(this.dataset.giftLimit);
      this.thresholdTwo = parseFloat(this.dataset.giftLimitTwo);
      this.variantId = parseInt(this.dataset.variantId);
      this.variantIdTwo = parseInt(this.dataset.variantIdTwo);
      this.giftCombineType = this.dataset.giftType;
    }

    connectedCallback() {
      this.initGWP();
      this.addEventListeners();
    }

    async initGWP() {
      
      this.cart = await this.getCartData();
      if (!this.cart) return;

      await this.testThreshold(this.cart);
    }

    addEventListeners() {
      let debounceTimeout = null;
      document.addEventListener('cart:update', () => {
        clearTimeout(debounceTimeout);
        debounceTimeout = setTimeout(() => this.initGWP(), 650);
      });
    }

    async testThreshold(cart) {
      this.cartTotal = cart.total_price;
      this.giftInCart = cart.items.find((item) => item.variant_id === this.variantId);
      this.giftInCartTwo = cart.items.find((item) => item.variant_id === this.variantIdTwo);
  
      // if (!this.thresholdOne) return; // Ensure the first threshold is defined otherwise this is likely only a shipping bar
  
      let updates = this.giftCombineType === 'combine' 
          ? this.setUpdatesCombine() 
          : this.setUpdatesReplace();
  
      await this.autoUpdateCart(updates); // Apply updates to the cart
    }
    
    setUpdatesCombine() {
      let updates = {};
      // Handle gift at first threshold
      if(this.variantId) {
        if (this.cartTotal >= this.thresholdOne) {
          if (!this.giftInCart) {
            updates[this.variantId] = 1; // Add the gift if not already in the cart
          }
        } else if (this.giftInCart) {
          updates[this.variantId] = 0; // Remove the gift if below the threshold
        }
      }
  
      // Handle gift at second threshold
      if(this.variantIdTwo) {
        if (this.cartTotal >= this.thresholdTwo) {
        if (!this.giftInCartTwo) {
            updates[this.variantIdTwo] = 1; // Add the second gift if not already in the cart
          }
        } else if (this.giftInCartTwo) {
          updates[this.variantIdTwo] = 0; // Remove the gift if below the threshold
        }
      }
  
      return updates;
    }
    
    setUpdatesReplace() {
      let updates = {};
  
      // Handle higher threshold (replace lower gift if applicable)
      if (this.cartTotal >= this.thresholdTwo) {
        if (this.giftInCartTwo && this.variantIdTwo) {
          updates[this.variantIdTwo] = 1; // Ensure second gift is in the cart
        }
        if (this.giftInCart && this.variantId) {
          updates[this.variantId] = 0; // Remove the first gift
        }
      } else if (this.cartTotal >= this.thresholdOne && this.cartTotal < this.thresholdTwo) {
        if (this.giftInCart && this.variantId) {
            updates[this.variantId] = 1; // Ensure first gift is in the cart
        }
        if (this.giftInCartTwo && this.variantIdTwo) {
            updates[this.variantIdTwo] = 0; // Remove the second gift
        }
      } else {
        // If below both thresholds, remove any gifts
        if (this.giftInCart && this.variantId) {
          updates[this.variantId] = 0;
        }
        if (this.giftInCartTwo && this.variantIdTwo) {
            updates[this.variantIdTwo] = 0;
        }
      }

      return updates;
    }

    #setLoaderActive(active) {
      const loader = document.querySelector('.cart-drawer__overlay');
      if (loader) loader.classList.toggle('active', active);
    }

    autoUpdateCart(updates) {
      if (Object.keys(updates).length === 0) return;

      if (this.checkoutButton) this.checkoutButton.setAttribute('disabled', 'disabled');

      this.#setLoaderActive(true);

      const formData = {
        updates
      };

      const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
        .map(el => el.dataset.sectionId)
        .filter(Boolean);
      if (sectionIds.length) formData.sections = sectionIds.join(',');

      fetch(`${Theme.routes.cart_update_url}.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData) // ✅ no extra nesting
      })
        .then(res => res.json().then(data => {
          if (!res.ok) throw new Error(data.message || 'Failed to update cart');
          return data;
        }))
        .then(data => {
          // Work out itemCount from the updates object
          let itemCount = 0;
          for (const [variantId, qty] of Object.entries(updates)) {
            if (qty > 0) {
              itemCount += qty;
            }
          }

          const id = this.dataset.id;
          this.dispatchEvent(new CartAddEvent({}, id.toString(), {
            source: 'cart-progress',
            itemCount,
            productId: this.dataset.productId,
            sections: data.sections,
          }));
          if (this.checkoutButton) this.checkoutButton.removeAttribute('disabled');
          this.#setLoaderActive(false);
        })
        .catch(error => {
          console.error('Cart update error:', error);
          if (this.checkoutButton) this.checkoutButton.removeAttribute('disabled');
          this.#setLoaderActive(false);
        });
    }

    async getCartData() {
      return fetch(`${Theme.routes.cart_url}.js`, {
        method: 'GET'
      })
      .then(response => {
        return response.json();
      })
      .catch((error) => {
        console.error('Error:', error);
        return false;
      });
    }
  };

  window.customElements.define('cart-progress', CartProgress);
}
